import React, { useState } from 'react';
import Modal from '../Common/Modal';
import ExplorationModeSelector from './ExplorationModeSelector';
import IconPicker from './IconPicker';
import { customFocusApi } from '../../api/customFocus';
import './CustomFocus.css';

/**
 * CreateCustomFocusModal - Modal for creating a new custom focus area
 *
 * Props:
 * - isOpen: Whether modal is open
 * - onClose: Callback to close modal
 * - onCreated: Callback when focus is created (receives new focus)
 * - storyId: Story ID for story-level focus
 * - projectId: Project ID for project-level focus
 */
export default function CreateCustomFocusModal({
  isOpen,
  onClose,
  onCreated,
  storyId,
  projectId,
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [explorationMode, setExplorationMode] = useState('develop');
  const [areasToExplore, setAreasToExplore] = useState('');
  const [icon, setIcon] = useState('pencil');
  const [scope, setScope] = useState(storyId ? 'story' : 'project');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const data = {
        name: name.trim(),
        description: description.trim() || undefined,
        exploration_mode: explorationMode,
        areas_to_explore: areasToExplore.trim() || undefined,
        icon,
      };

      const targetId = scope === 'story' ? storyId : projectId;
      let newFocus;

      if (scope === 'story') {
        newFocus = await customFocusApi.createForStory(targetId, data);
      } else {
        newFocus = await customFocusApi.createForProject(targetId, data);
      }

      onCreated(newFocus);
      handleClose();
    } catch (err) {
      console.error('Failed to create custom focus:', err);
      setError(err.message || 'Failed to create custom focus');
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setName('');
    setDescription('');
    setExplorationMode('develop');
    setAreasToExplore('');
    setIcon('pencil');
    setScope(storyId ? 'story' : 'project');
    setError(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create Custom Focus"
      footer={
        <div className="create-focus-actions">
          <button
            type="button"
            className="create-focus-cancel-btn"
            onClick={handleClose}
            disabled={isCreating}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="create-focus-submit-btn"
            onClick={handleSubmit}
            disabled={isCreating || !name.trim()}
          >
            {isCreating ? 'Creating...' : 'Create Focus'}
          </button>
        </div>
      }
    >
      <form className="create-focus-form" onSubmit={handleSubmit}>
        {error && <div className="create-focus-error">{error}</div>}

        <div className="create-focus-field">
          <label htmlFor="focus-name">Name *</label>
          <input
            id="focus-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Magic System, Character Voice, Timeline"
            maxLength={100}
            autoFocus
          />
        </div>

        <div className="create-focus-field">
          <label htmlFor="focus-description">Description</label>
          <textarea
            id="focus-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional: Brief description of what this focus covers"
            rows={2}
          />
        </div>

        <ExplorationModeSelector
          value={explorationMode}
          onChange={setExplorationMode}
        />

        <div className="create-focus-field">
          <label htmlFor="focus-areas">Areas to Explore</label>
          <textarea
            id="focus-areas"
            value={areasToExplore}
            onChange={(e) => setAreasToExplore(e.target.value)}
            placeholder="Optional: Specific aspects you want to explore"
            rows={2}
          />
        </div>

        <IconPicker value={icon} onChange={setIcon} />

        {/* Only show scope selector if both story and project are available */}
        {storyId && projectId && (
          <div className="create-focus-field">
            <label>Scope</label>
            <div className="scope-selector">
              <label className={`scope-option ${scope === 'story' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="scope"
                  value="story"
                  checked={scope === 'story'}
                  onChange={() => setScope('story')}
                />
                <span>This story only</span>
              </label>
              <label className={`scope-option ${scope === 'project' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="scope"
                  value="project"
                  checked={scope === 'project'}
                  onChange={() => setScope('project')}
                />
                <span>All stories in project</span>
              </label>
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
}

export { CreateCustomFocusModal };
